import { Alert, Button, CloseButton, TextInput } from "@mantine/core";
import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import {
  addShiftSession,
  removeShiftSession,
  updateShiftSession,
} from "../../../../data/actions/shiftActions";
import { IState } from "../../../../data/reducers/state";
import {
  IShiftSession,
} from "../../../../types/shifts/Shift";
import { adjustFinishTimeForStartChange, adjustStartTimeForFinishChange, formatDateTime, parseDateTimeLocalInput, toDateTime, toDateTimeNullable } from "../../../../util/date+helper";
// import DateTimeRangeContainer from 'react-advanced-datetimerange-picker'
import { ShiftSessionLink } from "../../../common/Links";
import { SSTable } from "../../../common/TableData";

interface ISessionEditorProps {
  id?: string | null;
  shiftId?: string | null;
  sessions?: IShiftSession[];
  onEdit?: (sessions: IShiftSession[]) => void;

  actions?: {
    removeShiftSession: (
      shiftId: string,
      sessionId: string,
    ) => Promise<boolean>;
    updateShiftSession: (
      session: Partial<IShiftSession>,
    ) => Promise<IShiftSession>;
    addShiftSession: (session: IShiftSession) => Promise<IShiftSession>;
  };
  associationTz?: string | null;
}

interface ISessionRowProps {
  idx: number;
  session: IShiftSession;
  startEdit: () => void;
  onChange: (session: Partial<IShiftSession>) => Promise<IShiftSession | void>;
  onRemove: () => Promise<boolean | void>;
  tz?: string | undefined;
}

const NonEditableRow: React.FunctionComponent<ISessionRowProps> = ({
  idx,
  session,
  startEdit,
  tz,
}) => (
  <tr>
    <td style={{ verticalAlign: "middle" }}>{idx + 1}</td>
    <td style={{ verticalAlign: "middle" }}>
      {formatDateTime(session.start, tz)}
    </td>
    <td style={{ verticalAlign: "middle" }}>
      {formatDateTime(session.finish, tz)}
    </td>
    <td style={{ verticalAlign: "middle" }}>
      {session.shift ? (
        <ShiftSessionLink
          shiftId={session.shift}
          sessionId={session._id}
          openInNewTab
        >
          View
        </ShiftSessionLink>
      ) : (
        ""
      )}{" "}
      |
      <Button
        onClick={startEdit}
        color="link"
        style={{
          color: "inherit",
          fontSize: "14px",
          padding: "0 5px",
          verticalAlign: "baseline",
        }}
      >
        Edit
      </Button>
    </td>
  </tr>
);

const EditableRow: React.FunctionComponent<ISessionRowProps> = ({
  session,
  onChange,
  onRemove,
  tz,
}) => {
  const [range, editRange] = React.useState<{
    start?: string | null;
    finish?: string | null;
  }>({
    start: toDateTimeNullable(session.start)?.toISO() || null,
    finish: toDateTimeNullable(session.finish)?.toISO() || null,
  });

  const editStart = (value: string | null | undefined) => {
    // handle empty/cleared value
    // When value comes from an <input type="datetime-local" />, it's a local
    // date/time string with no zone. Interpret it in the association tz so
    // the displayed/edited time is association-local.
    const newStart = parseDateTimeLocalInput(value, tz);
    if (!newStart) {
      editRange({ start: null, finish: range.finish });
      return;
    }

    const currentFinish = toDateTimeNullable(range.finish, tz);
    const newFinish = adjustFinishTimeForStartChange(newStart, currentFinish, tz);

    editRange({
      start: newStart.toUTC().toISO(),
      finish: newFinish.toUTC().toISO(),
    });
  };

  const editFinish = (value: string | null | undefined) => {
    const newFinish = parseDateTimeLocalInput(value, tz);
    if (!newFinish) {
      editRange({ start: range.start, finish: null });
      return;
    }

    const currentStart = toDateTimeNullable(range.start, tz);
    const newStart = adjustStartTimeForFinishChange(newFinish, currentStart, tz);

    editRange({
      start: newStart.toUTC().toISO(),
      finish: newFinish.toUTC().toISO(),
    });
  };

  // const prepareChange = ({ currentTarget }) => onChange(currentTarget.name, new Date(currentTarget.value).toISOString())
  const start = toDateTimeNullable(range.start || session.start, tz);
  const finish = toDateTimeNullable(range.finish || session.finish, tz);
  return (
    <tr>
      <td>
        <CloseButton onClick={onRemove} aria-label="Remove session" />
      </td>
      <td>
        <TextInput
          name="start"
          type="datetime-local"
          onChange={(e) => editStart(e.currentTarget.value)}
          value={start ? start.toFormat("yyyy-MM-dd'T'HH:mm") : ""}
        />
      </td>
      <td>
        <TextInput
          name="finish"
          type="datetime-local"
          onChange={(e) => editFinish(e.currentTarget.value)}
          value={finish ? finish.toFormat("yyyy-MM-dd'T'HH:mm") : ""}
        />
      </td>
      <td>
        <Button
          size="sm"
          variant="outline"
          disabled={!start && !finish}
          onClick={() => onChange({ start: start ? start.toJSDate() : undefined, finish: finish ? finish.toJSDate() : undefined })}
        >
          Okay
        </Button>
      </td>
    </tr>
  );
};

const SessionEditor: React.FunctionComponent<ISessionEditorProps> = ({
  sessions,
  shiftId,
  onEdit,
  actions,
  associationTz,
}) => {
  if (!shiftId) {
    return (
      <p>
        An internal error occurred (no ShiftId was present on SessionEditor)
      </p>
    );
  }
  const [rowToEdit, setRowToEdit] = React.useState(-1);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const showTzWarning = associationTz && localTz && associationTz !== localTz;

  const onChange: (
    sessionId: string,
    changes: Partial<IShiftSession>,
  ) => Promise<IShiftSession | void> = (sessionId, changes) => {
    const body = { ...changes, _id: sessionId };
    if (!actions || !actions.updateShiftSession) {
      setRowToEdit(-1);
      return Promise.resolve(undefined);
    }
    return actions
      .updateShiftSession(body)
      .then((resp) => {
        console.log("Changed and received", resp);
        setRowToEdit(-1);
        return resp;
      })
      .catch((er) => {
        setErrorMessage(er.message);
      });
  };

  const onRemove: (idx: number) => Promise<boolean> = (idx: number) => {
    if (!sessions) {
      setRowToEdit(-1);
      return Promise.resolve(false);
    }
    const id = sessions[idx]._id;
    if (!id) {
      setRowToEdit(-1);
      if (onEdit) onEdit([...sessions.slice(0, idx), ...sessions.slice(idx + 1)]);
      return Promise.resolve(true);
    }
    if (!actions || !actions.removeShiftSession) {
      setRowToEdit(-1);
      return Promise.resolve(false);
    }
    return actions
      .removeShiftSession(shiftId, id)
      .then((resp) => {
        // store is updated a automatically
        setRowToEdit(-1);
        return resp;
      })
      .catch((er) => {
        setErrorMessage(er.message);
      }) as Promise<boolean>;
  };

  const addSession = () => {
    let start = DateTime.local().plus({ day: 1 }).set({ hour: 16, minute: 0 }).setZone(associationTz || undefined);
    let finish = DateTime.local().plus({ day: 1 }).set({ hour: 23, minute: 0 }).setZone(associationTz || undefined);

    const prev = sessions && sessions.length > 0 ? sessions[sessions.length - 1] : null;
    if (prev) {
      // prev.start/finish may be null - guard and fallback to now-based values
      try {
        if (prev.start) start = toDateTime(prev.start, associationTz || undefined).plus({ week: 1 });
        if (prev.finish) finish = toDateTime(prev.finish, associationTz || undefined).plus({ week: 1 });
      } catch (e) {
        // fall back to computed defaults
      }
    }

    if (!actions || !actions.addShiftSession) {
      return;
    }
    actions
      .addShiftSession({
        start: start.toJSDate(),
        finish: finish.toJSDate(),
        shift: shiftId,
      } as IShiftSession)
      .catch((er) => {
        setErrorMessage(er.message);
      });
  };

  const idFromRow = (idx: number) => `session-date-range-row-${idx}`;

  // helper props passed to EditableRow to format in association tz
  return (
    <div>
      <Button
        variant="outline"
        size="sm"
        onClick={addSession}
        style={{ float: "right", marginBottom: "10px" }}
      >
        NEW
      </Button>
      {showTzWarning && (
        <Alert color="yellow">
          Times are displayed in {associationTz}. Your computer is set to {localTz}.
        </Alert>
      )}
      <SSTable>
        <thead>
          <tr>
            <td>#</td>
            <td>Start</td>
            <td>Finish</td>
            <td />
            {/* <td>Roster</td>
                        <td>Edit</td> */}
          </tr>
        </thead>
        <tbody>
          {(sessions || []).map((r, idx) =>
            React.createElement(idx === rowToEdit ? EditableRow : NonEditableRow, {
              key: idFromRow(idx),
              idx,
              session: r,
              onRemove: () => onRemove(idx),
              startEdit: () => setRowToEdit(idx),
              onChange: (changes) => onChange(r._id, changes),
              tz: associationTz || undefined,
            }),
          )}
        </tbody>
      </SSTable>
      {errorMessage && <Alert color="red">{errorMessage}</Alert>}
      {/* <Popover trigger="legacy" placement="bottom" isOpen={rowToEdit >= 0} target={rowToEdit >= 0 ? idFromRow(rowToEdit) : null} toggle={() => setRowToEdit(-1)}>
            <PopoverBody>
                <p>{cSession ? "Has session" : "No session"}</p>

                {cSession && <DateTimeRangeContainer
                    start={toDateTime(cSession.start)}
                    end={toDateTime(cSession.finish)}
                    local={{"format":"DD-MM-YYYY HH:mm","sundayFirst" : false}}
                    applyCallback={console.log}
                >
                <p>Test</p>

                </DateTimeRangeContainer>}
            </PopoverBody>
        </Popover> */}
    </div>
  );
};

function mapState2Props(
  state: IState,
  props: ISessionEditorProps,
): Partial<ISessionEditorProps> {
  if (!props.shiftId) return {};
  const sessions = state.shiftSessions.byShiftId[props.shiftId]?.map(
    (s) => state.shiftSessions.map[s],
  );

  // find the shift to get associationId -> association.config.tz
  const shift = state.shifts.map[props.shiftId];
  const associationId = shift ? shift.associationId : undefined;
  const association = associationId ? state.associations.map[associationId] : undefined;
  const associationTz = association && association.config ? association.config.tz : undefined;

  return {
    sessions,
    associationTz,
  };
}

function mapDispatchToProps(dispatch: any): Partial<ISessionEditorProps> {
  return {
    // actions: bindActionCreators({
    actions: {
      removeShiftSession: (shiftId: string, sessionId: string) =>
        dispatch(removeShiftSession(shiftId, sessionId)),
      updateShiftSession: (session: Partial<IShiftSession>) =>
        dispatch(updateShiftSession(session)),
      addShiftSession: (shiftSession: IShiftSession) =>
        dispatch(addShiftSession(shiftSession)),
    },
  };
}
export default connect(mapState2Props, mapDispatchToProps)(SessionEditor);
